<script setup lang="ts">

import { TextField } from '@library/Components/Forms/TextField';
import { BankDetails, CarbucksAmount, ProfileHeader } from '../Components';
import { SectionTitle, useBackButton } from "@app/Components/Utilities";
import { AddBankAccountPromotionTile } from "../Promotion";
import { ButtonGroup } from '@library/Components/Forms/ButtonGroup';
import { useRedeemCashbackViewModel } from '../Types/RedeemCashbackViewModel';
import { currency } from '@library/Utilities';

useBackButton();

const model = await useRedeemCashbackViewModel();

</script>

<template>
    <div class="h-full flex flex-col bg-white dark:bg-gray-900 overflow-scroll">

        <ProfileHeader v-model="model.carbucks.profile" :is-marketplace="false" />

        <div class="flex flex-col gap-3 lg:gap-4 mt-3 lg:mt-4 mb-8">
            <CarbucksAmount v-model="model.carbucks.profile" />
        </div>

        <div class="flex-grow pt-3">
            
            <AddBankAccountPromotionTile v-model="model.carbucks.profile" v-if="!model.carbucks.profile.hasBankAccountDetails" />

            <div class="flex flex-col h-full" v-else>
                <div class="flex-grow">

                    <div class="bg-red-500/20 border border-red-700 rounded p-4 mb-6 mx-3" v-if="model.errorState.value">
                        {{ model.errorState.value }}
                    </div>

                    <section class="px-3 mb-8">
                        <SectionTitle>Withdraw amount</SectionTitle>
                        <div v-if="!model.carbucks.profile.hasCashbackThatCanBeRedeemed" class="p-3 bg-gray-500 rounded text-gray-900">
                            Minimum balance of {{ currency( model.carbucks.profile.MinimumCarbucksBeforeRedemption ) }} is required before you can withdraw.
                        </div>
                        <div v-else>
                            <ButtonGroup color="secondary-500" mandatory class="grid grid-cols-5 space-x-3 !h-auto" v-model="model.buttonState.value">
                                <Button class="!rounded grow py-3" value="lowest" :variant="model.buttonState.value === 'lowest' ? 'flat' : 'tonal'" @click="() => model.updatePayoutAmount(model.quickSetButtons.lowest)">
                                    {{ currency( model.quickSetButtons.lowest ) }}
                                </Button>
                                <Button class="!rounded grow" value="middle" :variant="model.buttonState.value === 'middle' ? 'flat' : 'tonal'" @click="() => model.updatePayoutAmount(model.quickSetButtons.middle)">
                                    {{ currency( model.quickSetButtons.middle ) }}
                                </Button>
                                <Button class="!rounded grow" value="highest" :variant="model.buttonState.value === 'highest' ? 'flat' : 'tonal'" @click="() => model.updatePayoutAmount(model.quickSetButtons.highest)">
                                    {{ currency( model.quickSetButtons.highest ) }}
                                </Button>
                                <Button class="!rounded grow col-span-2" value="custom" :variant="model.buttonState.value === 'custom' ? 'flat' : 'tonal'" @click="() => model.updatePayoutAmount(0)">
                                    Custom
                                </Button>
                            </ButtonGroup>
                        </div>
                        <div class="mt-6" v-if="model.buttonState.value === 'custom'">
                            <TextField label="Transfer Amount" type="number" v-model="model.payoutAmount.value" prefix="$" />
                        </div>
                    </section>

                    <section>
                        <SectionTitle class="px-3">Bank Account</SectionTitle>
                        <div class="bg-gray-400/20 border-t border-gray-50 dark:border-gray-700">
                            <BankDetails v-model="model.carbucks.profile.bankAccount" />
                        </div>
                    </section>
                    
                </div>
            </div>
        </div>

        <div class="text-center mt-6 mb-6 px-16">
            <Button variant="flat" color="secondary" size="large" class="w-full" :disabled="!model.carbucks.profile.hasCashbackThatCanBeRedeemed || !model.carbucks.profile.hasBankAccountDetails" @click="model.performPayout()">Withdraw</Button>
        </div>
    </div>
</template>